import React, { useEffect, useMemo, useState } from "react";
import { FiEdit3, FiMail, FiMapPin, FiPhone, FiShield, FiUser } from "react-icons/fi";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { BaseUrl } from "../shared/BaseUrl";
import profileImg from "../image/profile.png";
import "./ProfilePage.css";

const EMPTY_PROFILE = {
  username: "",
  email: "",
  phoneNum: "",
  location: "",
  profileImageUrl: "",
};

const normalizeProfile = (payload) => ({
  username: payload?.username || "",
  email: payload?.email || "",
  phoneNum: payload?.phoneNum || "",
  location: payload?.location || "",
  profileImageUrl: payload?.profileImageUrl || "",
});

const ProfilePage = ({ onProfileUpdated }) => {
  const token = localStorage.getItem("token");
  const isTokenValid = Boolean(token && token !== "null" && token !== "undefined");

  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [formState, setFormState] = useState(EMPTY_PROFILE);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [statsState, setStatsState] = useState({
    qrCodes: 0,
    activeContent: 0,
    securityStatus: "Standard",
  });
  const [isStatsLoading, setIsStatsLoading] = useState(true);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isTokenValid) {
        setIsProfileLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BaseUrl}/api/auth/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const normalized = normalizeProfile(response?.data?.data);
        setProfile(normalized);
        setFormState(normalized);
        localStorage.setItem("username", normalized.username || "");
        localStorage.setItem("email", normalized.email || "");
        localStorage.setItem("profileImageUrl", normalized.profileImageUrl || "");
        if (onProfileUpdated) {
          onProfileUpdated(normalized);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Failed to load profile details", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsProfileLoading(false);
      }
    };

    fetchProfile();
  }, [isTokenValid, token]);

  useEffect(() => {
    const fetchProfileStats = async () => {
      if (!isTokenValid) {
        setStatsState({
          qrCodes: 0,
          activeContent: 0,
          securityStatus: "Standard",
        });
        setIsStatsLoading(false);
        return;
      }

      setIsStatsLoading(true);

      try {
        const qrResponse = await axios.get(`${BaseUrl}/api/qrcode/user/qr`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const qrId = qrResponse?.data?._id;
        if (!qrId) {
          setStatsState({
            qrCodes: 0,
            activeContent: 0,
            securityStatus: "No QR Yet",
          });
          return;
        }

        const contentsResponse = await axios.get(
          `${BaseUrl}/api/content/contents/${qrId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const contents = contentsResponse?.data?.contents || [];
        const currentContentId = contentsResponse?.data?.currentContent?._id || null;
        const activeContentCount = contents.filter(
          (item) => item._id === currentContentId || item.status === "active"
        ).length;
        const hasProtectedContent = contents.some((item) => item.isSecure);

        setStatsState({
          qrCodes: qrId ? 1 : 0,
          activeContent: activeContentCount,
          securityStatus: hasProtectedContent ? "Protected" : "Standard",
        });
      } catch (error) {
        if (error?.response?.status === 400 || error?.response?.status === 404) {
          setStatsState({
            qrCodes: 0,
            activeContent: 0,
            securityStatus: "No QR Yet",
          });
        } else {
          console.error("Error loading profile stats:", error);
          toast.error("Unable to load profile stats right now.", {
            position: "top-right",
            autoClose: 3000,
          });
        }
      } finally {
        setIsStatsLoading(false);
      }
    };

    fetchProfileStats();
  }, [isTokenValid, token]);

  const validateForm = () => {
    const username = formState.username.trim();
    const email = formState.email.trim();
    const phoneNum = formState.phoneNum.trim();
    const location = formState.location.trim();

    if (!username) {
      return "Username is required";
    }
    if (username.length < 3 || username.length > 30) {
      return "Username must be between 3 and 30 characters";
    }

    if (!email) {
      return "Email is required";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Please enter a valid email address";
    }

    if (phoneNum && !/^[+0-9()\-.\s]{7,20}$/.test(phoneNum)) {
      return "Phone number format is invalid";
    }

    if (location.length > 120) {
      return "Location must be 120 characters or fewer";
    }

    return "";
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedImage(null);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
        setImagePreviewUrl("");
      }
      return;
    }

    if (!file.type.startsWith("image/")) {
      setFormError("Only image files are allowed");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setFormError("Profile image must be 5MB or smaller");
      return;
    }

    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setSelectedImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    setFormError("");
  };

  const handleCancelEdit = () => {
    setFormState(profile);
    setSelectedImage(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl("");
    }
    setFormError("");
    setIsEditing(false);
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (!isTokenValid) {
      setFormError("Please log in again to update your profile");
      return;
    }

    setIsSaving(true);
    setFormError("");

    try {
      const formData = new FormData();
      formData.append("username", formState.username.trim());
      formData.append("email", formState.email.trim());
      formData.append("phoneNum", formState.phoneNum.trim());
      formData.append("location", formState.location.trim());
      if (selectedImage) {
        formData.append("profileImage", selectedImage);
      }

      const response = await axios.put(`${BaseUrl}/api/auth/profile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const normalized = normalizeProfile(response?.data?.data);
      setProfile(normalized);
      setFormState(normalized);
      setIsEditing(false);
      setSelectedImage(null);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
        setImagePreviewUrl("");
      }

      localStorage.setItem("username", normalized.username || "");
      localStorage.setItem("email", normalized.email || "");
      localStorage.setItem("profileImageUrl", normalized.profileImageUrl || "");
      if (onProfileUpdated) {
        onProfileUpdated(normalized);
      }

      toast.success(response?.data?.message || "Profile updated successfully", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to update profile";
      setFormError(message);
      toast.error(message, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const profileStats = useMemo(
    () => [
      {
        label: "QR Codes",
        value: isStatsLoading ? "..." : statsState.qrCodes.toLocaleString(),
      },
      {
        label: "Active Content",
        value: isStatsLoading ? "..." : statsState.activeContent.toLocaleString(),
      },
      {
        label: "Security Status",
        value: isStatsLoading ? "..." : statsState.securityStatus,
      },
    ],
    [isStatsLoading, statsState]
  );

  const accountInfo = [
    { icon: FiUser, label: "Username", value: profile.username || "N/A" },
    { icon: FiMail, label: "Email", value: profile.email || "N/A" },
    { icon: FiPhone, label: "Phone", value: profile.phoneNum || "Not set" },
    { icon: FiMapPin, label: "Location", value: profile.location || "Not set" },
  ];

  if (!isTokenValid) {
    return (
      <main className="profile-page">
        <div className="profile-shell">
          <section className="profile-panel profile-auth-panel">
            <h2>Profile Access Required</h2>
            <p>Please log in to view and edit your profile.</p>
            <Link to="/login" className="profile-link-btn">
              Go to Login
            </Link>
          </section>
        </div>
      </main>
    );
  }

  const avatarSrc = imagePreviewUrl || profile.profileImageUrl || profileImg;
  const profileTitle = profile.username || "TattQR User";

  return (
    <main className="profile-page">
      <div className="profile-shell">
        <header className="profile-hero">
          <div className="profile-identity">
            <div className="profile-ring">
              <img src={avatarSrc} alt="Profile" className="profile-avatar" />
            </div>
            <div className="profile-title">
              <p className="profile-kicker">Account</p>
              <h1>{isProfileLoading ? "Loading..." : profileTitle}</h1>
              <p className="profile-subtitle">
                Manage your profile, account security, and creator identity.
              </p>
            </div>
          </div>
          <div className="profile-actions">
            <Link to="/" className="profile-link-btn">
              Back to Dashboard
            </Link>
            <button
              type="button"
              className="profile-edit-btn"
              onClick={() => setIsEditing(true)}
              disabled={isProfileLoading}
            >
              <FiEdit3 />
              Edit Profile
            </button>
          </div>
        </header>

        {isEditing && (
          <section className="profile-panel profile-edit-panel">
            <h2>Edit Profile</h2>
            <form onSubmit={handleSaveProfile} className="profile-edit-form">
              <div className="profile-edit-grid">
                <label className="profile-field">
                  <span>Username</span>
                  <input
                    type="text"
                    value={formState.username}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        username: event.target.value,
                      }))
                    }
                    maxLength={30}
                    required
                  />
                </label>

                <label className="profile-field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={formState.email}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        email: event.target.value,
                      }))
                    }
                    required
                  />
                </label>

                <label className="profile-field">
                  <span>Phone Number</span>
                  <input
                    type="text"
                    value={formState.phoneNum}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        phoneNum: event.target.value,
                      }))
                    }
                    placeholder="+1 555 555 5555"
                  />
                </label>

                <label className="profile-field">
                  <span>Location</span>
                  <input
                    type="text"
                    value={formState.location}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        location: event.target.value,
                      }))
                    }
                    maxLength={120}
                    placeholder="California, USA"
                  />
                </label>
              </div>

              <label className="profile-field profile-file-field">
                <span>Profile Image</span>
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/webp, image/gif"
                  onChange={handleImageChange}
                />
              </label>

              {formError && <p className="profile-form-error">{formError}</p>}

              <div className="profile-form-actions">
                <button
                  type="button"
                  className="profile-outline-btn"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="profile-edit-btn"
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </section>
        )}

        <section className="profile-stats">
          {profileStats.map((item) => (
            <article key={item.label} className="profile-stat-card">
              <span>{item.value}</span>
              <small>{item.label}</small>
            </article>
          ))}
        </section>

        <section className="profile-grid">
          <article className="profile-panel">
            <h2>Personal Information</h2>
            <div className="profile-info-list">
              {accountInfo.map((entry) => {
                const Icon = entry.icon;
                return (
                  <div key={entry.label} className="profile-info-row">
                    <span className="profile-info-icon">
                      <Icon />
                    </span>
                    <div>
                      <small>{entry.label}</small>
                      <p>{entry.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </article>

          <article className="profile-panel">
            <h2>Security & Access</h2>
            <div className="profile-security-card">
              <h3>
                <FiShield />
                Secure Content PIN
              </h3>
              <p>
                Protect premium or private QR content using PIN-gated access.
                Update your PIN regularly for stronger account security.
              </p>
              <button type="button" className="profile-outline-btn">
                Update Security Settings
              </button>
            </div>
            <div className="profile-note">
              <h4>Profile Note</h4>
              <p>
                Keep your public identity consistent so scanners can trust your
                content source when they open shared QR experiences.
              </p>
            </div>
          </article>
        </section>
      </div>
    </main>
  );
};

export default ProfilePage;
